import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import type { Orientation } from "../hooks/useBannerLogic";
import { useTranslation } from "react-i18next";

const PREFIX = "BannerPlaceholder";

const classes = {
  portraitIndicator: `${PREFIX}-portraitIndicator`,
  albumIndicator: `${PREFIX}-albumIndicator`,
  initialState: `${PREFIX}-initialState`,
  loadingState: `${PREFIX}-loadingState`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.portraitIndicator}`]: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "35%",
    paddingTop: "52.5%", // 35% * (3/2) для пропорции 2:3
    height: 0,
    borderRadius: 4,
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255, 255, 255, 0.04)",
      border: "1px dashed rgba(255, 255, 255, 0.15)",
    }),
    ...theme.applyStyles("light", {
      backgroundColor: "rgba(0, 0, 0, 0.025)",
      border: "1px dashed rgba(0, 0, 0, 0.1)",
    }),
    zIndex: 0,
    "&::after": {
      content: '"2:3"',
      position: "absolute",
      bottom: 4,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: "8px",
      color: theme.palette.text.secondary,
      fontWeight: 500,
      opacity: 0.5,
    },
  },

  [`& .${classes.albumIndicator}`]: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "70%",
    paddingTop: "33.75%",
    height: 0,
    borderRadius: 4,
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255, 255, 255, 0.04)",
      border: "1px dashed rgba(255, 255, 255, 0.15)",
    }),
    ...theme.applyStyles("light", {
      backgroundColor: "rgba(0, 0, 0, 0.025)",
      border: "1px dashed rgba(0, 0, 0, 0.1)",
    }),
    zIndex: 0,
    "&::after": {
      content: '"16:9"',
      position: "absolute",
      bottom: 4,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: "8px",
      color: theme.palette.text.secondary,
      fontWeight: 500,
      opacity: 0.5,
    },
  },

  [`& .${classes.initialState}`]: {
    position: "absolute",
    textAlign: "center",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    cursor: "pointer",
    zIndex: 1,
  },

  [`& .${classes.loadingState}`]: {
    position: "absolute",
    textAlign: "center",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 2,
  },
}));

interface BannerPlaceholderProps {
  orientation: Orientation;
  label?: string;
  required?: boolean;
  onUploadClick: () => void;
  isLoading?: boolean;
}

export function BannerPlaceholder({
  orientation,
  label,
  required,
  onUploadClick,
  isLoading = false,
}: BannerPlaceholderProps) {
  const { t } = useTranslation();

  return (
    <Root>
      {orientation === "vertical" && (
        <div className={classes.portraitIndicator} />
      )}
      {orientation === "horizontal" && (
        <div className={classes.albumIndicator} />
      )}
      <div
        className={classes.initialState}
        onClick={onUploadClick}
        role="button"
      >
        <CloudUploadIcon />
        <Typography variant="subtitle1" color="textSecondary">
          {label || t("shared.banner.upload", { defaultValue: "Загрузить" })}
          {required && " *"}
        </Typography>
      </div>
      {isLoading && (
        <div className={classes.loadingState}>
          <CircularProgress />
        </div>
      )}
    </Root>
  );
}
